// Persistent client-side store for the user's pinned/favourited Product
// Shot scene templates. Keyed by scene id (e.g. "template3"). Read by
// /templates (browse + toggle) and by StepBackground (surfacing the
// "Pinned for you" tab).
//
// Why localStorage and not Supabase: pins are a personal browsing
// preference, low-stakes, and surviving across devices isn't critical
// for v1. If we ever want cross-device sync, migrate the same shape to
// a user_pinned_templates table — the lib API stays the same.

const STORAGE_KEY = 'socialposts.pinnedTemplates';

type Listener = (ids: string[]) => void;
const listeners = new Set<Listener>();

function safeRead(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

function safeWrite(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // quota / private-mode — silently skip, in-memory listeners still fire
  }
}

function emit(ids: string[]): void {
  for (const fn of listeners) fn(ids);
}

export function getPinned(): string[] {
  return safeRead();
}

export function isPinned(id: string): boolean {
  return safeRead().includes(id);
}

/** Toggle pin state for `id`. Returns the updated id list. */
export function togglePin(id: string): string[] {
  const current = safeRead();
  const next = current.includes(id)
    ? current.filter((x) => x !== id)
    : [...current, id];
  safeWrite(next);
  emit(next);
  return next;
}

/** Subscribe to pin-list changes. Fires whenever togglePin runs in this
 *  tab. Also listens for the native `storage` event so changes made in
 *  another tab propagate. Returns an unsubscribe function. */
export function subscribePinned(fn: Listener): () => void {
  listeners.add(fn);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) fn(safeRead());
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }
  return () => {
    listeners.delete(fn);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}
